import React, { useMemo } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import ToggleTabGroup from 'components/consumer/ToggleTabGroup';

const TabPanel = ({
  'aria-label': ariaLabel,
  items,
  selectedTab,
  onSelectTab = () => {},
  variant,
  classNameExtension,
  tabClassNamesOverride,
  tabGroupsClassNamesOverride,
  selectedTabClassNamesOverride,
  tabContentClassNamesOverride,
}) => {
  // Derived, not set in an effect, so the tabs are in the server HTML instead
  // of popping in after hydration and shifting the page
  const itemsToRender = useMemo(
    () =>
      (items || []).map(
        ({
          badgeValue,
          content,
          label,
          id,
          tabClass,
          customComponent,
          borderType,
        }) => {
          const contentId = `${id}-content`;

          return {
            badgeValue,
            content,
            contentId,
            id,
            label,
            customComponent,
            onClick: () => {
              onSelectTab(id);
            },
            tabClass,
            borderType,
          };
        }
      ),
    [items, onSelectTab]
  );

  const selectedItem = itemsToRender.find(({ id }) => id === selectedTab);

  return (
    <div
      className={cx('[border-top:1px_solid_#f1f1f1] md:[border-top:0]', {
        '[border-top:0]': variant === 'buttons' || variant === 'underline',
        ...(classNameExtension || {}),
      })}
    >
      <ToggleTabGroup
        aria-label={ariaLabel}
        items={itemsToRender}
        selectedTab={selectedTab}
        variant={variant}
        tabClassNamesOverride={tabClassNamesOverride}
        tabGroupsClassNamesOverride={tabGroupsClassNamesOverride}
        selectedTabClassNamesOverride={selectedTabClassNamesOverride}
      />

      {itemsToRender.map(item => {
        const { content = false, contentId, id: tabId } = item;

        return (
          <div
            aria-labelledby={tabId}
            data-xc={`TabPanelContent-${tabId}`}
            className={cx('pt-4 [&[hidden]]:hidden', {
              [tabContentClassNamesOverride]: !!tabContentClassNamesOverride,
            })}
            hidden={item !== selectedItem ? true : undefined}
            id={contentId}
            role="tabpanel"
            tabIndex="0"
            key={tabId}
          >
            {content}
          </div>
        );
      })}
    </div>
  );
};

TabPanel.propTypes = {
  'aria-label': PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      content: PropTypes.node,
      badgeValue: PropTypes.node,
      customComponent: PropTypes.node,
      borderType: PropTypes.oneOf(['bottom', 'all']),
    })
  ).isRequired,
  onSelectTab: PropTypes.func,
  selectedTab: PropTypes.string,
  variant: PropTypes.oneOf(['buttons', 'tabs', 'underline']),
  classNameExtension: PropTypes.string,
  tabClassNamesOverride: PropTypes.string,
  tabGroupsClassNamesOverride: PropTypes.string,
  selectedTabClassNamesOverride: PropTypes.string,
  tabContentClassNamesOverride: PropTypes.string,
};

export default TabPanel;
